<template lang='pug'>
  .slider-container(
    :data-ga-event='gaOptions ? gaOptions.event : null'
    :data-ga-action='gaOptions ? gaOptions.action : null'
    :data-ga-class='gaClasses'
    @mouseenter='sliderPause'
    @mouseleave='sliderPlay'
  )
    .arrows(v-if='showArrows')
      a.arrow.left(:class='{ gaClasses, disable: isFirstSlide }' @click='goTo("prev")')
        .svg-icon.chevron-left
      a.arrow.right(:class='{ gaClasses, disable: isLastSlide }' @click='goTo("next")')
        .svg-icon.chevron-left

    slot(name='slider-content')
</template>

<script>
import { tns } from 'tiny-slider/src/tiny-slider'
import isNil from 'lodash/isNil'

export default {
  props: {
    // carouselType will be needed for scaling to billboard carousel
    carouselType: {
      type: String,
      required: true
    },
    autoplay: {
      type: Boolean,
      required: true
    },
    items: {
      type: Number,
      required: true
    },
    mouseDrag: {
      type: Boolean,
      default: true
    },
    nav: {
      type: Boolean,
      required: true
    },
    slideBy: {
      type: Number,
      required: true
    },
    speed: {
      type: Number,
      required: true
    },
    containerClass: {
      type: String,
      required: true
    },
    showArrows: {
      type: Boolean,
      required: true
    },
    responsiveOptions: {
      type: Object,
      default: () => {}
    },
    gaOptions: {
      type: Object,
      default: () => {}
    },
    loop: {
      type: Boolean,
      default: true
    },
    rewind: {
      type: [Boolean, null, undefined]
    },
    navAsThumbnails: {
      type: Boolean,
      default: false
    },
    navContainer: {
      type: String,
      default: null
    },
    controls: {
      type: Boolean,
      default: false
    },
    // needed to correctly indentify the nav controls for the current slider, this should not clash with other nav controls
    controlsContainer: {
      type: [String, Boolean],
      default: false
    }
  },

  data () {
    return {
      slider: null,
      currentSlideIndex: 0
    }
  },

  computed: {
    gaClasses () {
      return this.gaOptions ? this.combineClasses(this.gaOptions.classes) : null
    },
    isFirstSlide () {
      return !this.loop && this.currentSlideIndex === 0
    },
    isLastSlide () {
      const info = this.slider?.getInfo()
      return !this.loop && this.currentSlideIndex >= info?.slideCount - info?.items
    }
  },

  mounted () {
    const sliderOptions = {
      autoplay: this.autoplay,
      autoplayButton: false,
      autoplayButtonOutput: false,
      autoplayTimeout: 7000,
      container: `.${this.containerClass}`,
      items: this.items,
      loop: this.loop,
      mouseDrag: this.mouseDrag,
      nav: this.nav,
      navAsThumbnails: this.navAsThumbnails,
      navContainer: this.navContainer ? `.${this.navContainer}` : false,
      navPosition: 'bottom',
      controls: this.controls,
      controlsContainer: this.controls ? `.${this.controlsContainer}` : false,
      rewind: isNil(this.rewind) ? !this.loop : this.rewind,
      slideBy: this.slideBy,
      speed: this.speed,
      responsive: { ...this.responsiveOptions }
    }
    this.slider = tns(sliderOptions)
    this.$nextTick(() => {
      if (this.slider) {
        this.slider.events.on('indexChanged', this.handleIndexChanged)
        this.$emit('cloneCountChanged', this.slider.getInfo().cloneCount)
      }
    })
  },

  destroyed () {
    if (this.slider) {
      this.slider.events.off('indexChanged', this.handleIndexChanged)
    }
  },

  activated () {
    if (this.slider) {
      this.slider.events.on('indexChanged', this.handleIndexChanged)
    }
  },

  deactivated () {
    if (this.slider) {
      this.slider.events.off('indexChanged', this.handleIndexChanged)
    }
  },

  methods: {
    goTo (direction) {
      this.slider.goTo(direction)
    },
    sliderPlay () {
      this.slider.play()
    },
    sliderPause () {
      this.slider.pause()
    },
    combineClasses (classes) {
      return classes ? classes.join(' ') : null
    },
    handleIndexChanged (event) {
      this.currentSlideIndex = event.displayIndex - 1
      this.$emit('indexChanged', this.currentSlideIndex)
    }
  }
}
</script>

<style lang='scss'>
  @import '~tiny-slider/src/tiny-slider';

  .tns-item {
    padding-left: 5px;
    padding-right: 5px;

    @include media-breakpoint-up(sm) {
      padding-left: 18.5px;
      padding-right: 18.5px;
    }
  }

  .tns-nav {
    text-align: center;

    [aria-controls] {
      background: $grey;
      width: 12px;
      height: 12px;
      margin-left: 5px;
      margin-right: 5px;
      border: 0;
      border-radius: 50%;

      &.tns-nav-active {
        background: $grey-dark;
      }
    }
  }

  .slider-container {
    width: 100%;
    height: auto;
    position: relative;
    overflow: hidden;
    opacity: 1;
    box-sizing: border-box;
    user-select: none;

    ::-webkit-scrollbar,
    -webkit-scrollbar {
      @include media-breakpoint-down(sm) {
        opacity: 0;
        visibility: hidden;
      }
    }

    .wrapper {
      overflow: hidden;
      margin: auto;

      @include media-breakpoint-up(sm) {
        width: calc(100% - 80px);
      }
    }

    .arrow {
      width: 40px;
      height: 60px;
      padding-bottom: 10px;
      padding-top: 10px;
      top: calc(50% - 30px);
      position: absolute;
      line-height: 0;

      cursor: pointer;

      &.left {
        left: 0;
      }

      &.right {
        right: 0;
        transform: rotate(180deg);
      }

      &.disable {
        opacity: 0.3;
        cursor: initial;
        pointer-events: none;
      }
    }
  }
</style>
